import { useCallback, useEffect, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as z from 'zod';
import { CURRENCY_USD } from 'constants/currency';
import {
  CoachAvailabilityStatusesEnum,
  GetCoachSettingsQuery,
  useUpdateUserOnCourtLessonServiceMutation,
} from 'types/generated/client';
import { convertFormattedPriceToUnitAmount } from 'utils/shared/money/convertFormattedPriceToUnitAmount';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getCurrencySymbol } from 'utils/shared/money/getCurrencySymbol';
import { useViewer } from 'hooks/useViewer';
import { EditScheduleRef } from 'screens/MySchedule/EditSchedule';
import Button from 'components/Button';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import BookingTypeSwitch, { BookingTypeEnum } from './BookingTypeSwitch';
import DirectBookingContent from './DirectBookingContent';
import ExternalBookingContent from './ExternalBookingContent';
import { Step3Skeleton } from '../Skeletons';

interface ScheduleStepProps {
  onSubmit: () => void;
  coachData?: GetCoachSettingsQuery['usersByPk'];
  isLoading?: boolean;
}

const scheduleStepSchema = z.object({
  priceUnitAmountCoachDefault: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number().max(10000).min(0)),
  directBookings: z.boolean().default(false),
  externalBookingUrl: z.string().optional(),
  description: z.string().optional(),
});

type ScheduleStepForm = z.infer<typeof scheduleStepSchema>;

const ScheduleStep = ({ onSubmit, coachData, isLoading }: ScheduleStepProps) => {
  const timeSlotFormRef = useRef<EditScheduleRef>(null);
  const viewer = useViewer();
  const [bookingType, setBookingType] = useState<BookingTypeEnum>(BookingTypeEnum.DirectOnBounce);
  const [updateUserOnCourtLessonService, { loading: isUpdating }] =
    useUpdateUserOnCourtLessonServiceMutation();

  const currency =
    coachData?.stripeMerchantCurrency || coachData?.currencyCoachDefault || CURRENCY_USD;
  const currencySymbol = getCurrencySymbol(currency);

  const {
    control,
    register,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ScheduleStepForm>({
    resolver: zodResolver(scheduleStepSchema),
    defaultValues: {
      priceUnitAmountCoachDefault: undefined,
      directBookings: false,
      externalBookingUrl: '',
      description: '',
    },
  });

  const directBookings = useWatch({ name: 'directBookings', control });

  const handleSaveAndContinue = useCallback(
    async (data: ScheduleStepForm) => {
      try {
        if (!timeSlotFormRef.current) {
          toast.error('Schedule form is not available');
          return;
        }
        const [isValidated, timeSlotsData] = await timeSlotFormRef.current.validateAndGetValues();

        let timeSlots = timeSlotsData.reducedData;

        let finalTimeSlots = null;

        if (bookingType === BookingTypeEnum.DirectOnBounce) {
          // For direct bookings, validate schedule if direct bookings is enabled

          if (directBookings && (!isValidated || !timeSlots?.length)) {
            toast.error('Please set up your schedule');
            return;
          }

          // For direct bookings, preserve the existing status of time slots
          finalTimeSlots = timeSlots;
        } else {
          // For external bookings, validate external URL
          if (!data.externalBookingUrl || data.externalBookingUrl.trim() === '') {
            setError('externalBookingUrl', {
              type: 'required',
              message: 'External booking URL is required',
            });
            return;
          }

          // Validate URL format
          try {
            new URL(data.externalBookingUrl);
          } catch {
            setError('externalBookingUrl', {
              type: 'pattern',
              message: 'Please enter a valid URL',
            });
            return;
          }

          // For external bookings, deactivate all time slots to disable direct booking
          finalTimeSlots = timeSlots.map((timeSlot) => ({
            ...timeSlot,
            status: CoachAvailabilityStatusesEnum.Inactive,
          }));
        }

        // Prepare mutation variables
        const mutationVariables = {
          userId: viewer.userId,
          priceUnitAmountCoachDefault: data.priceUnitAmountCoachDefault
            ? convertFormattedPriceToUnitAmount(Number(data.priceUnitAmountCoachDefault), currency)
            : 0,
          objects: finalTimeSlots || [],
          coachExternalBookingUrl:
            bookingType === BookingTypeEnum.ExternalLink ? data.externalBookingUrl || '' : '',
        };

        // Execute mutation
        const result = await updateUserOnCourtLessonService({
          variables: mutationVariables,
          refetchQueries: ['getCurrentUser'],
        });

        if (!result.errors) {
          toast.success('Lesson settings saved successfully!');
          onSubmit(); // Move to next step
        } else {
          toast.error('Failed to save lesson settings. Please try again.');
        }
      } catch (error) {
        console.error('Error saving lesson settings:', error);
        captureException(error);
        toast.error('An error occurred. Please try again.');
      }
    },
    [
      viewer.userId,
      currency,
      updateUserOnCourtLessonService,
      onSubmit,
      bookingType,
      setError,
      directBookings,
    ],
  );

  // Pre-populate form with existing data
  useEffect(() => {
    if (coachData && !isLoading) {
      // Set pricing
      if (coachData.priceUnitAmountCoachDefault) {
        const formattedPrice = convertUnitPriceToFormattedPrice(
          coachData.priceUnitAmountCoachDefault,
          coachData.stripeMerchantCurrency || coachData.currencyCoachDefault || CURRENCY_USD,
        );
        setValue('priceUnitAmountCoachDefault', formattedPrice.priceFormatted);
      }

      if (coachData.coachExternalBookingUrl) {
        setBookingType(BookingTypeEnum.ExternalLink);
        setValue('externalBookingUrl', coachData.coachExternalBookingUrl);
      }

      // Set direct bookings based on coach availability
      if (coachData.coachAvailability && coachData.coachAvailability.length > 0) {
        // Check if there's at least one ACTIVE availability
        const hasActiveAvailability = coachData.coachAvailability.some(
          (availability) => availability.status === CoachAvailabilityStatusesEnum.Active,
        );
        setValue('directBookings', hasActiveAvailability);
      } else {
        // Empty array or no availability - default to true for first-time users
        setValue('directBookings', true);
      }

      // Set description
      if (coachData.coachLessonDescription) {
        setValue('description', coachData.coachLessonDescription);
      }

      // Set external booking URL if available
      if (coachData.coachExternalBookingUrl) {
        setValue('externalBookingUrl', coachData.coachExternalBookingUrl);
        setBookingType(BookingTypeEnum.ExternalLink);
      }
    }
  }, [coachData, isLoading, setValue]);

  // Clear external booking URL errors when switching tabs
  useEffect(() => {
    clearErrors('externalBookingUrl');
  }, [bookingType, clearErrors]);

  if (isLoading) {
    return <Step3Skeleton />;
  }

  // Shared price input field that will be passed to both child components
  const priceInputField = (
    <InputField
      renderIcon={() => (
        <span className="text-color-text-darkmode-secondary">{currencySymbol}</span>
      )}
      inputProps={{
        type: 'number',
        placeholder: '',
      }}
      fieldLabel=""
      errors={errors}
      placeholder=""
      name="priceUnitAmountCoachDefault"
      register={register}
      inputType={InputType.RegularWithIcon}
      errorBorder={true}
      invert
    />
  );

  return (
    <div className="mt-[4.2rem] flex flex-col gap-2 max-md:mt-[3.75rem]">
      <div className="flex flex-col items-center justify-center gap-9">
        <h2 className="typography-product-heading-compact-desktop text-color-text-lightmode-invert dark:text-color-text-darkmode-invert">
          How would you like players book your lessons?
        </h2>
        <BookingTypeSwitch
          className="max-w-[21.5rem]"
          activeType={bookingType}
          onTypeChange={setBookingType}
        />
      </div>
      {/* Tab Content */}

      <DirectBookingContent
        control={control}
        errors={errors}
        directBookings={directBookings}
        coachData={coachData}
        timeSlotFormRef={timeSlotFormRef}
        visible={bookingType === BookingTypeEnum.DirectOnBounce}
        priceInputField={bookingType === BookingTypeEnum.DirectOnBounce ? priceInputField : null}
      />

      <ExternalBookingContent
        register={register}
        errors={errors}
        visible={bookingType === BookingTypeEnum.ExternalLink}
        priceInputField={bookingType === BookingTypeEnum.ExternalLink ? priceInputField : null}
      />

      {/* hidden for mvp */}
      {/* <ProfileItem
        customClass="max-md:mx-0"
        label="Description"
        description="Provide general description to your private on-court lessons."
        invert
        hasBottomBorder={false}
        component={
          <InputField
            fieldLabel=""
            errors={errors}
            placeholder="Text"
            name="description"
            register={register}
            inputType={InputType.Textarea}
            errorBorder={true}
            invert
          />
        }
      /> */}

      <div className="flex flex-col items-center justify-center pt-8 max-md:mt-0 max-md:pt-0">
        <Button
          variant="brand"
          onClick={handleSubmit(handleSaveAndContinue)}
          sizeDesktop="lg"
          sizeMobile="md"
          className="w-fit"
          loading={isUpdating}
        >
          {isUpdating ? 'Saving...' : 'Save and continue'}
        </Button>
        <div className="typography-product-caption max-sm:typography-product-chips-filters mt-ds-xl text-center text-color-text-darkmode-tertiary">
          Don't worry — you can edit your information anytime by clicking on your avatar and
          selecting 'My Services' from the dropdown menu. And Schedule - from the Calendar.
        </div>
      </div>
    </div>
  );
};

export default ScheduleStep;
